/** Path item packaging / required-qty cost helpers (BOM catalog). */

export const PATH_ITEM_DETAILS_OPTIONS = ["Box", "Bottle", "Packet", "Each"];

const QTY_UNIT_BY_PACKAGING = {
  Box: "piece",
  Bottle: "ml",
  Packet: "gm",
  Each: "piece",
};

export const normalizePathItemDetailsKey = (pathItemDetails) =>
  String(pathItemDetails || "").trim().toLowerCase();

/** qty_unit value from packaging type (Box → piece, Bottle → ml, …). */
export const resolvePathItemQtyUnit = (pathItemDetails) => {
  const key = normalizePathItemDetailsKey(pathItemDetails);
  if (key === "box") return QTY_UNIT_BY_PACKAGING.Box;
  if (key === "bottle") return QTY_UNIT_BY_PACKAGING.Bottle;
  if (key === "packet") return QTY_UNIT_BY_PACKAGING.Packet;
  if (key === "each") return QTY_UNIT_BY_PACKAGING.Each;
  return "";
};

/** @deprecated Use resolvePathItemQtyUnit — kept for older imports. */
export const resolvePathItemUnitType = resolvePathItemQtyUnit;

export const isEachPathItem = (pathItemDetails) =>
  normalizePathItemDetailsKey(pathItemDetails) === "each";

/** Box / Bottle / Packet need a packaging-size input; Each is always 1. */
export const usesPathItemDetailsMultiplier = (pathItemDetails) => {
  const key = normalizePathItemDetailsKey(pathItemDetails);
  return key === "box" || key === "bottle" || key === "packet";
};

export const getPathItemDetailsFieldLabel = (pathItemDetails) => {
  const key = normalizePathItemDetailsKey(pathItemDetails);
  if (key === "box") return "Per Box Pieces";
  if (key === "bottle") return "Per Bottle Ml";
  if (key === "packet") return "Per Packet Gm";
  return "Details";
};

/** Parse decimal-friendly numbers (0.5, 1.2). Never use parseInt for BOM qty/cost. */
export const parseDecimalNumber = (value, fallback = 0) => {
  if (value === "" || value === null || value === undefined) return fallback;
  if (typeof value === "number") {
    return Number.isFinite(value) ? value : fallback;
  }
  const normalized = String(value).trim().replace(/,/g, "");
  if (!normalized) return fallback;
  const parsed = Number.parseFloat(normalized);
  return Number.isFinite(parsed) ? parsed : fallback;
};

export const resolvePathItemDetailsValue = (pathItemDetails, detailsInput) => {
  if (isEachPathItem(pathItemDetails)) return 1;
  const parsed = parseDecimalNumber(detailsInput, NaN);
  if (!Number.isFinite(parsed) || parsed <= 0) return 0;
  return parsed;
};

/**
 * Cost for required quantity per test =
 * (package cost / packaging size) × required quantity per test
 * e.g. (₹202 / 500 ml) × 0.5 ml = ₹0.202 → rounded to ₹0.20
 */
export const calculatePathItemCostForRequiredQuantity = (
  pathItemDetails,
  cost,
  detailsInput,
  requiredQuantity
) => {
  const costNum = parseDecimalNumber(cost, NaN);
  const details = resolvePathItemDetailsValue(pathItemDetails, detailsInput);
  const reqQty = parseDecimalNumber(requiredQuantity, NaN);
  if (
    !Number.isFinite(costNum) ||
    costNum < 0 ||
    details <= 0 ||
    !Number.isFinite(reqQty) ||
    reqQty < 0
  ) {
    return 0;
  }
  const raw = (costNum / details) * reqQty;
  return Math.round(raw * 100) / 100;
};

export const formatPathItemCost = (value, fractionDigits = 2) => {
  const num = Number(value);
  if (!Number.isFinite(num)) return "";
  return num.toFixed(fractionDigits);
};

export const getPathItemCostPreviewLabel = (
  pathItemDetails,
  cost,
  details,
  requiredQuantity
) => {
  const costForReq = calculatePathItemCostForRequiredQuantity(
    pathItemDetails,
    cost,
    details,
    requiredQuantity
  );
  if (!costForReq) return "";

  const unit = resolvePathItemQtyUnit(pathItemDetails) || "unit";
  const reqQty = parseDecimalNumber(requiredQuantity, NaN);
  if (!Number.isFinite(reqQty) || reqQty <= 0) return "";

  return `Cost for ${reqQty} ${unit}: ${formatPathItemCost(costForReq)}`;
};

/** Legacy packaging was temporarily stored in description as `Bottle|500`. */
export const parsePathItemDescription = (description) => {
  const str = String(description || "").trim();
  if (!str) return { pathItemDetails: "", details: "" };

  const pipeMatch = str.match(/^(Box|Bottle|Packet|Each)\|(.+)$/i);
  if (pipeMatch) {
    const pathItemDetails =
      PATH_ITEM_DETAILS_OPTIONS.find(
        (opt) => opt.toLowerCase() === pipeMatch[1].toLowerCase()
      ) || "";
    const rawDetails = String(pipeMatch[2] || "").trim();
    const parsed = Number(rawDetails);
    return {
      pathItemDetails,
      details:
        Number.isFinite(parsed) && parsed > 0
          ? String(parsed)
          : isEachPathItem(pathItemDetails)
            ? "1"
            : "",
    };
  }

  const bare = PATH_ITEM_DETAILS_OPTIONS.find(
    (opt) => opt.toLowerCase() === str.toLowerCase()
  );
  if (bare) {
    return {
      pathItemDetails: bare,
      details: isEachPathItem(bare) ? "1" : "",
    };
  }

  return { pathItemDetails: "", details: "" };
};

export const isLegacyPackagingDescription = (description) => {
  const parsed = parsePathItemDescription(description);
  return Boolean(parsed.pathItemDetails);
};

/** Normalize packaging type (unit_type): Box / Bottle / Packet / Each. */
export const normalizePathItemPackagingType = (value) => {
  if (value == null || value === "") return "";
  const key = String(value).trim().toLowerCase();
  return (
    PATH_ITEM_DETAILS_OPTIONS.find((opt) => opt.toLowerCase() === key) || ""
  );
};

/** Infer packaging type from legacy qty-only unitType (ml → Bottle). */
export const inferPathItemDetailsFromQtyUnit = (qtyUnit) => {
  const key = String(qtyUnit || "")
    .trim()
    .toLowerCase()
    .replace(/s$/, "");
  if (key === "ml") return "Bottle";
  if (key === "gm" || key === "g") return "Packet";
  if (key === "piece" || key === "pc") return "";
  return normalizePathItemPackagingType(qtyUnit);
};

/** @deprecated Use inferPathItemDetailsFromQtyUnit */
export const inferPathItemDetailsFromUnitType = inferPathItemDetailsFromQtyUnit;

/** packaging size = (package cost × required qty per test) / cost for that qty */
export const reconstructPathItemDetailsFromCost = (
  cost,
  costForRequiredQuantity,
  requiredQuantity
) => {
  const costNum = parseDecimalNumber(cost, NaN);
  const costForReq = parseDecimalNumber(costForRequiredQuantity, NaN);
  const reqQty = parseDecimalNumber(requiredQuantity, NaN);
  if (
    !Number.isFinite(costNum) ||
    !Number.isFinite(costForReq) ||
    !Number.isFinite(reqQty) ||
    costForReq <= 0 ||
    reqQty <= 0
  ) {
    return "";
  }
  const raw = (costNum * reqQty) / costForReq;
  if (!Number.isFinite(raw) || raw <= 0) return "";
  const rounded = Math.round(raw * 1000) / 1000;
  return String(rounded);
};

/**
 * Read packaging type (unit_type) from BOM — prefers unitType when it is Box/Bottle/…
 * Falls back to legacy description encoding or qty-unit inference.
 */
export const getBomPackagingType = (bom) => {
  if (!bom) return "";
  const fromUnitType = normalizePathItemPackagingType(bom.unitType ?? bom.unit_type);
  if (fromUnitType) return fromUnitType;

  const fromDesc = parsePathItemDescription(bom.description).pathItemDetails;
  if (fromDesc) return fromDesc;

  return inferPathItemDetailsFromQtyUnit(bom.qtyUnit ?? bom.qty_unit ?? bom.unitType);
};

/**
 * Read qty_unit (piece / ml / gm) from BOM.
 * Prefers qtyUnit; falls back to resolving from packaging type or legacy unitType.
 */
export const getBomQtyUnit = (bom) => {
  if (!bom) return "";
  const explicit = bom.qtyUnit ?? bom.qty_unit;
  if (explicit != null && String(explicit).trim() !== "") {
    return String(explicit).trim();
  }

  const packaging = getBomPackagingType(bom);
  if (packaging) return resolvePathItemQtyUnit(packaging);

  const legacy = bom.unitType ?? bom.unit_type;
  const legacyKey = String(legacy || "")
    .trim()
    .toLowerCase();
  if (legacyKey === "ml") return "ml";
  if (legacyKey === "gm" || legacyKey === "g") return "gm";
  if (legacyKey === "piece" || legacyKey === "pieces" || legacyKey === "pc") {
    return "piece";
  }
  return "";
};

export const getBomDetails = (bom) => {
  if (!bom) return "";
  const explicit = bom.details;
  if (explicit != null && String(explicit).trim() !== "") {
    return String(explicit);
  }
  const fromDesc = parsePathItemDescription(bom.description).details;
  if (fromDesc) return fromDesc;
  return "";
};

/** Packaging size for stock math (Each / missing → 1). */
export const getBomPackagingDetailsNumber = (bom) => {
  const parsed = parseDecimalNumber(getBomDetails(bom), NaN);
  if (Number.isFinite(parsed) && parsed > 0) return parsed;
  return 1;
};

export const getBomPackageCost = (bom) => parseDecimalNumber(bom?.cost, 0);

/**
 * Convert package count (bottles/boxes/packets) into stock qty + system price.
 * Example: 2 bottles × 500 ml @ ₹202 → quantity 1000, price 404.
 */
export const calculatePathStockFromPackages = (packageQuantity, bom) => {
  const packages = parseDecimalNumber(packageQuantity, 0);
  const details = getBomPackagingDetailsNumber(bom);
  const packageCost = getBomPackageCost(bom);
  const qtyUnit = getBomQtyUnit(bom) || "unit";
  const packagingType = getBomPackagingType(bom);

  return {
    packages,
    details,
    packageCost,
    qtyUnit,
    packagingType,
    quantity: packages * details,
    price: Math.round(packageCost * packages * 100) / 100,
  };
};

export const getPathStockConversionPreview = (packageQuantity, bom) => {
  if (!bom) return "";
  const calc = calculatePathStockFromPackages(packageQuantity, bom);
  if (!calc.packages || calc.packages <= 0) return "";
  const packLabel = calc.packagingType
    ? calc.packagingType.toLowerCase()
    : "package";
  return `Stock: ${calc.quantity} ${calc.qtyUnit} (${calc.packages} × ${calc.details} ${calc.qtyUnit}/${packLabel}) | System price: ${calc.price}`;
};

/** Caption under quantity — same idea as medicine inventory stock math. */
export const getPathStockQuantityCaption = (packageQuantity, bom) => {
  if (!bom) return "";
  const calc = calculatePathStockFromPackages(packageQuantity, bom);
  if (!calc.packages || calc.packages <= 0) return "";
  return `Entered qty (${calc.packages}) × ${calc.details} ${calc.qtyUnit} = ${calc.quantity} ${calc.qtyUnit}`;
};

/** Caption under system price — mirrors medicine "cost per strip/bottle". */
export const getPathStockUnitPriceCaption = (bom, packageCostOverride) => {
  if (!bom) return "";
  const calc = calculatePathStockFromPackages(1, {
    ...bom,
    cost:
      packageCostOverride != null && packageCostOverride !== ""
        ? packageCostOverride
        : bom.cost,
  });
  const packLabel = (calc.packagingType || "unit").toLowerCase();
  return `cost per ${packLabel}: ${calc.packageCost}`;
};

export const buildPathItemFormValuesFromBom = (bom) => {
  if (!bom) {
    return {
      name: "",
      pathItemDetails: "",
      details: "",
      qtyUnit: "",
      requiredQuantity: "",
      cost: "",
      costPerUnit: "",
      description: "",
    };
  }

  let pathItemDetails = getBomPackagingType(bom);
  let details = getBomDetails(bom);
  let qtyUnit = getBomQtyUnit(bom);

  if (!details && pathItemDetails) {
    if (isEachPathItem(pathItemDetails)) {
      details = "1";
    } else {
      details = reconstructPathItemDetailsFromCost(
        bom.cost,
        bom.costPerUnit ?? bom.cost_per_unit,
        bom.requiredQuantity ?? bom.required_quantity
      );
    }
  }

  if (!qtyUnit && pathItemDetails) {
    qtyUnit = resolvePathItemQtyUnit(pathItemDetails);
  }

  const rawDescription = bom.description || "";
  const description = isLegacyPackagingDescription(rawDescription)
    ? ""
    : rawDescription;

  return {
    name: bom.name || "",
    pathItemDetails: pathItemDetails || "",
    details: details || "",
    qtyUnit: qtyUnit || "",
    requiredQuantity: bom.requiredQuantity ?? bom.required_quantity ?? "",
    cost: bom.cost ?? "",
    costPerUnit: bom.costPerUnit ?? bom.cost_per_unit ?? "",
    description,
  };
};

/**
 * Build BOM create/update payload.
 * unitType  → packaging (Box/Bottle/Packet/Each) → DB unit_type
 * qtyUnit   → piece/ml/gm → DB qty_unit
 * details   → per-box pieces / per-bottle ml / … → DB details
 * description → free comment only (not packaging)
 */
export const buildPathItemSavePayload = (values, { existingDescription } = {}) => {
  const pathItemDetails = String(values.pathItemDetails || "").trim();
  const details = resolvePathItemDetailsValue(pathItemDetails, values.details);
  const cost = parseDecimalNumber(values.cost, 0);
  const requiredQuantity = parseDecimalNumber(values.requiredQuantity, 0);
  const costPerUnit = calculatePathItemCostForRequiredQuantity(
    pathItemDetails,
    cost,
    details,
    requiredQuantity
  );
  const qtyUnit =
    resolvePathItemQtyUnit(pathItemDetails) ||
    String(values.qtyUnit || values.unitType || "").trim();

  let description =
    values.description != null ? String(values.description).trim() : "";
  if (!description && existingDescription) {
    description = isLegacyPackagingDescription(existingDescription)
      ? ""
      : String(existingDescription).trim();
  }

  return {
    name: String(values.name || "").trim(),
    unitType: pathItemDetails,
    qtyUnit,
    details,
    requiredQuantity,
    cost,
    costPerUnit,
    description,
  };
};
